/**
 * Tour Tracking Hook
 *
 * Reads/writes walkthrough completion state to Firestore.
 * Each user gets a document tracking which pages they've toured.
 *
 * CRM Firestore path: referral-crm/data/walkthroughTracking/{userId}
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { TourTrackingState, TourPageState } from './types';

const COLLECTION_PATH = 'referral-crm/data/walkthroughTracking';

interface UseTourTrackingResult {
  tracking: TourTrackingState | null;
  loading: boolean;
  /** Mark a page as toured (completed) */
  markToured: (pageId: string, tourVersion: number) => Promise<void>;
  /** Mark a page as skipped */
  markSkipped: (pageId: string) => Promise<void>;
  /** Set global opt-out */
  setGlobalOptOut: (optOut: boolean) => Promise<void>;
  /** Reset tracking for a specific page (re-trigger tour) */
  resetPage: (pageId: string) => Promise<void>;
}

export function useTourTracking(): UseTourTrackingResult {
  const { user } = useAuth();
  const [tracking, setTracking] = useState<TourTrackingState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const docRef = doc(db, COLLECTION_PATH, user.id);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as TourTrackingState;
          setTracking(data);
        } else {
          setTracking({ pages: {}, globalOptOut: false });
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useTourTracking] Error:', err);
        setTracking({ pages: {}, globalOptOut: false });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.id]);

  const updateTracking = useCallback(
    async (updates: Partial<TourTrackingState & { updatedAt: unknown }>) => {
      if (!user?.id) return;
      const docRef = doc(db, COLLECTION_PATH, user.id);
      await setDoc(docRef, { ...updates, updatedAt: serverTimestamp() }, { merge: true });
    },
    [user?.id]
  );

  const markToured = useCallback(
    async (pageId: string, tourVersion: number) => {
      const pageState: TourPageState = {
        toured: true,
        skipped: false,
        lastTouredAt: new Date().toISOString(),
        tourVersion,
      };
      await updateTracking({ pages: { ...tracking?.pages, [pageId]: pageState } });
    },
    [tracking?.pages, updateTracking]
  );

  const markSkipped = useCallback(
    async (pageId: string) => {
      const pageState: TourPageState = {
        toured: false,
        skipped: true,
      };
      await updateTracking({ pages: { ...tracking?.pages, [pageId]: pageState } });
    },
    [tracking?.pages, updateTracking]
  );

  const setGlobalOptOut = useCallback(
    async (optOut: boolean) => {
      await updateTracking({ globalOptOut: optOut });
    },
    [updateTracking]
  );

  const resetPage = useCallback(
    async (pageId: string) => {
      const pages = { ...tracking?.pages };
      delete pages[pageId];
      await updateTracking({ pages });
    },
    [tracking?.pages, updateTracking]
  );

  return { tracking, loading, markToured, markSkipped, setGlobalOptOut, resetPage };
}
